import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Calendar, ClipboardCheck, X, RefreshCw, AlertCircle, Lock, Clock } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { formatSessionDate, formatSessionTime, tzToday, addDaysStr } from "@/utils/datetime";

export default function GroupDetail() {
    const { t } = useTranslation();
    const { id } = useParams();
    const nav = useNavigate();
    const { hasPerm, activeMembership } = useAuth();
    const tz = activeMembership?.tenant.timezone || "Asia/Baku";
    const canEditMarked = hasPerm("attendance.edit_closed") || hasPerm("attendance.manage");
    const isTeacherRole = activeMembership?.role?.code === "teacher";
    const teacherLocked = (s: any) => isTeacherRole && !canEditMarked && !!(s.attendance_marked || s.attendance_closed);

    // We drive each block independently: the whole page must not stay stuck
    // just because one API call errored. `groupErr` is fatal (no group ⇒ show
    // error state with retry); `rosterErr`/`rulesErr`/`sessionsErr` are per-
    // tab notices so the shell still renders.
    const [g, setG] = useState<any>(null);
    const [groupLoading, setGroupLoading] = useState(true);
    const [groupErr, setGroupErr] = useState<string | null>(null);
    const [tab, setTab] = useState<"roster" | "schedule" | "sessions">("roster");
    const [roster, setRoster] = useState<any[]>([]); const [rosterErr, setRosterErr] = useState<string | null>(null);
    const [rules, setRules] = useState<any[]>([]); const [rulesErr, setRulesErr] = useState<string | null>(null);
    const [sessions, setSessions] = useState<any[]>([]); const [sessionsErr, setSessionsErr] = useState<string | null>(null);
    const [markSession, setMarkSession] = useState<any>(null);
    const [showSched, setShowSched] = useState(false);
    // Phase ATT-1 — owner/admin can edit an enrollment's total lesson pool.
    const [creditsRow, setCreditsRow] = useState<any>(null);

    const load = useCallback(async () => {
        setGroupLoading(true);
        setGroupErr(null); setRosterErr(null); setRulesErr(null); setSessionsErr(null);
        const today = tzToday(tz);
        // Fetch the group first; without it we cannot render tabs meaningfully.
        try {
            const gg = await api.get(`/groups/${id}`);
            setG(gg.data);
        } catch (e: any) {
            setG(null);
            setGroupErr(formatApiError(e));
            setGroupLoading(false);
            return;
        }
        // The other three calls are independent — one failing must not blank
        // the page. Promise.allSettled + per-block error state keeps the tabs
        // usable.
        const results = await Promise.allSettled([
            api.get(`/groups/${id}/roster`),
            api.get(`/schedule/rules?group_id=${id}`),
            api.get(`/sessions?date_from=${addDaysStr(today, -14)}&date_to=${addDaysStr(today, 30)}&group_id=${id}`),
        ]);
        if (results[0].status === "fulfilled") setRoster(results[0].value.data.items || []); else setRosterErr(formatApiError((results[0] as any).reason));
        if (results[1].status === "fulfilled") setRules(results[1].value.data.items || []); else setRulesErr(formatApiError((results[1] as any).reason));
        if (results[2].status === "fulfilled") setSessions(results[2].value.data.items || []); else setSessionsErr(formatApiError((results[2] as any).reason));
        setGroupLoading(false);
    }, [id, tz]);
    useEffect(() => { load(); }, [load]);

    if (groupLoading && !g) return (
        <div className="flex justify-center p-8" data-testid="group-detail-loading"><Loader2 className="animate-spin" /></div>
    );
    if (groupErr || !g) return (
        <div className="space-y-4" data-testid="group-detail-error">
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm" style={{ color: "var(--text-muted)" }}><ArrowLeft size={14} /> {t("common.back")}</button>
            <div className="rounded-md p-4 text-sm flex items-start gap-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>
                <AlertCircle size={16} className="mt-0.5" />
                <div className="flex-1 space-y-2">
                    <div>{groupErr || t("common.empty")}</div>
                    <button data-testid="group-detail-retry" onClick={load} className="inline-flex items-center gap-1 text-xs rounded-md px-2 py-1 border" style={{ borderColor: "currentColor" }}>
                        <RefreshCw size={12} /> {t("common.retry", { defaultValue: "Yenidən cəhd et" })}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6" data-testid="group-detail">
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm" style={{ color: "var(--text-muted)" }}><ArrowLeft size={14} /> {t("common.back")}</button>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{g.name}</h1>
                    <p className="text-sm" style={{ color: "var(--text-muted)" }}>{g.enrolled_count} / {g.capacity} · {g.teachers.map((t2: any) => `${t2.first_name} ${t2.last_name}`).join(", ") || "—"}</p>
                </div>
                <button data-testid="group-detail-reload" onClick={load} className="inline-flex items-center gap-1 text-xs rounded-md px-2 py-1 border" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                    <RefreshCw size={12} /> {t("common.retry", { defaultValue: "Yenidən cəhd et" })}
                </button>
            </div>
            <div className="flex gap-2 border-b" style={{ borderColor: "var(--border)" }}>
                {(["roster", "schedule", "sessions"] as const).map((k) => (
                    <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className="px-4 py-2 text-sm border-b-2 transition-colors" style={{ color: tab === k ? "var(--text)" : "var(--text-muted)", borderColor: tab === k ? "var(--primary)" : "transparent" }}>{t(`groups.tab.${k}` as any)}</button>
                ))}
            </div>
            {tab === "roster" && (
                <div className="surface-card overflow-hidden" data-testid="roster-tab">
                    {rosterErr ? <TabError err={rosterErr} onRetry={load} /> : roster.length === 0 ? <div className="p-6 text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                        <table className="w-full text-sm">
                            <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                <th className="px-4 py-2">{t("common.name")}</th>
                                <th className="px-4 py-2">{t("common.status")}</th>
                                {/* Phase ATT-1: lesson balance columns */}
                                <th className="px-4 py-2 text-right">{t("roster.credits_total", { defaultValue: "Dərs yükü" })}</th>
                                <th className="px-4 py-2 text-right">{t("roster.attended", { defaultValue: "İştirak" })}</th>
                                <th className="px-4 py-2 text-right">{t("roster.remaining", { defaultValue: "Qalan" })}</th>
                                {hasPerm("enrollment.edit") && <th className="px-4 py-2"></th>}
                            </tr></thead>
                            <tbody>{roster.map((r) => (
                                <tr key={r.enrollment_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`roster-row-${r.person_id}`}>
                                    <td className="px-4 py-3" style={{ color: "var(--text)" }}>{r.person_name}</td>
                                    <td className="px-4 py-3"><span className={`status-pill ${r.is_waitlist ? "status-warning" : "status-success"}`}>{r.is_waitlist ? t("enrollment.waitlist") : t(`enrollment.status.${r.status}` as any, { defaultValue: r.status })}</span></td>
                                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--text)" }} data-testid={`roster-total-${r.person_id}`}>
                                        {r.lesson_credits_total == null ? <span style={{ color: "var(--text-muted)" }}>—</span> : r.lesson_credits_total}
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums" data-testid={`roster-attended-${r.person_id}`}>{r.lessons_attended ?? 0}</td>
                                    <td className="px-4 py-3 text-right" data-testid={`roster-remaining-${r.person_id}`}>
                                        {r.lesson_balance_remaining == null ? <span style={{ color: "var(--text-muted)" }}>—</span> : (
                                            <span className={`status-pill ${r.lesson_balance_remaining === 0 ? "status-warning" : r.lesson_balance_remaining <= 2 ? "status-info" : "status-neutral"}`}>{r.lesson_balance_remaining}</span>
                                        )}
                                    </td>
                                    {hasPerm("enrollment.edit") && (
                                        <td className="px-4 py-3 text-right">
                                            <button data-testid={`roster-edit-credits-${r.person_id}`} onClick={() => setCreditsRow(r)}
                                                className="text-xs inline-flex items-center gap-1 rounded-md px-2 py-1 border" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                                                {t("roster.edit_credits", { defaultValue: "Düzəliş" })}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                </div>
            )}
            {tab === "schedule" && (
                <div className="surface-card p-4 space-y-3" data-testid="schedule-tab">
                    {hasPerm("schedule.manage") && (
                        <div className="flex justify-end">
                            <button data-testid="schedule-edit-btn" onClick={() => setShowSched(true)} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md border" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                                <Calendar size={12} /> {rules.length ? t("groups.sched.edit") : t("groups.sched.title")}
                            </button>
                        </div>
                    )}
                    {rulesErr ? <TabError err={rulesErr} onRetry={load} /> : rules.length === 0 ? <div className="p-4 text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                        <ul className="space-y-2">{rules.map((r) => (
                            <li key={r.id} className="flex items-center gap-3 text-sm p-2 rounded-md" style={{ background: "var(--input)" }} data-testid={`rule-${r.id}`}>
                                <Calendar size={16} style={{ color: "var(--accent)" }} />
                                <span style={{ color: "var(--text)" }}>{t(`weekday.${r.weekday}` as any)}</span>
                                <span style={{ color: "var(--text-muted)" }}>{r.start_time}—{r.end_time}</span>
                            </li>
                        ))}</ul>
                    )}
                    <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("groups.sched.preserve_hint")}</p>
                </div>
            )}
            {showSched && <ScheduleEditor groupId={id!} rules={rules} onClose={() => setShowSched(false)} onSaved={() => { setShowSched(false); load(); }} />}
            {tab === "sessions" && (
                <div className="surface-card overflow-hidden" data-testid="sessions-tab">
                    {sessionsErr ? <TabError err={sessionsErr} onRetry={load} /> : sessions.length === 0 ? <div className="p-6 text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                        <table className="w-full text-sm">
                            <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                <th className="px-4 py-2">{t("session.date")}</th><th className="px-4 py-2">{t("session.time")}</th><th className="px-4 py-2">{t("common.status")}</th><th className="px-4 py-2 text-right">{t("common.actions")}</th>
                            </tr></thead>
                            <tbody>{sessions.map((s) => (
                                <tr key={s.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`session-${s.id}`}>
                                    <td className="px-4 py-3" style={{ color: "var(--text)" }}>{formatSessionDate(s.start_at, tz)}</td>
                                    <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{formatSessionTime(s.start_at, tz)}—{formatSessionTime(s.end_at, tz)}</td>
                                    <td className="px-4 py-3"><span className={`status-pill ${s.status === "cancelled" ? "status-error" : s.status === "held" ? "status-success" : "status-info"}`}>{t(`session.status.${s.status}` as any, { defaultValue: s.status })}</span></td>
                                    <td className="px-4 py-3 text-right">
                                        {hasPerm("attendance.mark") && s.status !== "cancelled" && (
                                            <AttendanceAction s={s} locked={teacherLocked(s)} onOpen={(ro) => setMarkSession({ ...s, readOnly: ro })} />
                                        )}
                                    </td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                </div>
            )}
                    {markSession && <MarkAttendanceDialog session={markSession} tz={tz} onClose={() => setMarkSession(null)} onSaved={() => { setMarkSession(null); load(); }} />}
                    {creditsRow && <EditCreditsDialog row={creditsRow} onClose={() => setCreditsRow(null)} onSaved={() => { setCreditsRow(null); load(); }} />}
        </div>
    );
}

function AttendanceAction({ s, locked, onOpen }: { s: any; locked: boolean; onOpen: (readOnly: boolean) => void }) {
    const { t } = useTranslation();
    const base = "text-xs inline-flex items-center gap-1 rounded-md px-2 py-1 border";
    if (new Date(s.start_at).getTime() > Date.now()) {
        return (
            <button data-testid={`mark-attendance-${s.id}`} disabled title={t("attendance.not_started")} className={`${base} cursor-not-allowed opacity-60`} style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                <Clock size={12} /> {t("attendance.not_started")}
            </button>
        );
    }
    if (locked) {
        return (
            <button data-testid={`mark-attendance-${s.id}`} data-locked="true" onClick={() => onOpen(true)} title={t("attendance.locked_hint")} className={base} style={{ borderColor: "var(--border)", color: "var(--text-muted)", background: "var(--input)" }}>
                <Lock size={12} /> {t("attendance.marked_locked")}
            </button>
        );
    }
    return (
        <button data-testid={`mark-attendance-${s.id}`} onClick={() => onOpen(false)} className={base} style={{ borderColor: "var(--border)", color: "var(--text)" }}>
            <ClipboardCheck size={12} /> {t("attendance.mark")}
        </button>
    );
}

function TabError({ err, onRetry }: { err: string; onRetry: () => void }) {
    const { t } = useTranslation();
    return (
        <div className="p-4 flex items-start gap-2 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="tab-error">
            <AlertCircle size={14} className="mt-0.5" />
            <div className="flex-1 space-y-2">
                <div>{err}</div>
                <button onClick={onRetry} data-testid="tab-error-retry" className="inline-flex items-center gap-1 text-xs rounded-md px-2 py-1 border" style={{ borderColor: "currentColor" }}>
                    <RefreshCw size={12} /> {t("common.retry", { defaultValue: "Yenidən cəhd et" })}
                </button>
            </div>
        </div>
    );
}

function MarkAttendanceDialog({ session, tz, onClose, onSaved }: any) {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const [items, setItems] = useState<any[]>([]); const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false); const [err, setErr] = useState<string | null>(null);
    const [marks, setMarks] = useState<Record<string, string>>({});
    useEffect(() => { (async () => { try { const { data } = await api.get(`/attendance/session/${session.id}`); setItems(data.items); const m: Record<string, string> = {}; data.items.forEach((it: any) => { m[it.person_id] = it.attendance?.status || "present"; }); setMarks(m); } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); } })(); }, [session.id]);
    const save = async () => { setSaving(true); setErr(null); try { await api.post(`/attendance/session/${session.id}`, { marks: items.map((it) => ({ person_id: it.person_id, status: marks[it.person_id] })) }); onSaved(); } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); } };
    const close_ = async () => { try { await api.post(`/attendance/session/${session.id}/close`); onSaved(); } catch (e: any) { setErr(formatApiError(e)); } };
    const STATUSES = ["present", "late", "excused_absence", "unexcused_absence"];
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="attendance-dialog">
            <div className="surface-card w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border)" }}>
                    <div><h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("attendance.mark")}</h2><p className="text-xs" style={{ color: "var(--text-muted)" }}>{formatSessionDate(session.start_at, tz)} · {formatSessionTime(session.start_at, tz)}—{formatSessionTime(session.end_at, tz)}</p></div>
                    <button onClick={onClose}><X size={18} style={{ color: "var(--text-muted)" }} /></button>
                </div>
                <div className="p-5 overflow-y-auto space-y-2">
                    {loading ? <Loader2 className="animate-spin mx-auto" /> : items.length === 0 ? <div className="text-sm text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : items.map((it) => (
                        <div key={it.person_id} className="flex items-center gap-2 flex-wrap" data-testid={`att-row-${it.person_id}`}>
                            <span className="flex-1 text-sm" style={{ color: "var(--text)" }}>{it.person_name}</span>
                            {STATUSES.map((st) => {
                                const on = marks[it.person_id] === st;
                                return <button key={st} data-testid={`att-${it.person_id}-${st}`} disabled={session.readOnly} onClick={() => setMarks({ ...marks, [it.person_id]: st })} className="rounded-md px-2 py-1 text-xs border disabled:cursor-not-allowed" style={{ background: on ? "var(--selected-bg)" : "var(--input)", color: on ? "var(--text)" : "var(--text-muted)", borderColor: on ? "var(--primary)" : "var(--border)", minHeight: 32 }}>{t(`attendance.status.${st}` as any, { defaultValue: st })}</button>;
                            })}
                        </div>
                    ))}
                    {err && <div className="text-sm" style={{ color: "var(--error-fg)" }} data-testid="att-error">{err}</div>}
                </div>
                {session.readOnly ? (
                <div className="p-5 border-t flex items-center gap-2 text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }} data-testid="att-locked-notice">
                    <Lock size={14} /> {t("attendance.locked_hint")}
                </div>
                ) : (
                <div className="p-5 border-t flex gap-2" style={{ borderColor: "var(--border)" }}>
                    <button data-testid="att-save" disabled={saving || items.length === 0} onClick={save} className="flex-1 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{saving ? <Loader2 className="animate-spin inline" size={16} /> : t("common.save")}</button>
                    {hasPerm("attendance.close") && !session.attendance_closed && <button data-testid="att-close" onClick={close_} className="rounded-md px-4 py-2 text-sm border" style={{ borderColor: "var(--border)", color: "var(--text)", minHeight: 44 }}>{t("attendance.close")}</button>}
                </div>
                )}
            </div>
        </div>
    );
}

function today(offsetDays: number): string {
    const d = new Date(); d.setDate(d.getDate() + offsetDays);
    return d.toISOString().slice(0, 10);
}

function ScheduleEditor({ groupId, rules, onClose, onSaved }: { groupId: string; rules: any[]; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const seed = rules[0] || {};
    const [state, setState] = useState<{ weekdays: number[]; start_time: string; end_time: string; effective_from: string; effective_until: string }>({
        weekdays: rules.map((r) => r.weekday).filter((w, i, a) => a.indexOf(w) === i).sort(),
        start_time: seed.start_time || "18:00",
        end_time: seed.end_time || "20:00",
        effective_from: seed.effective_from || today(0),
        effective_until: seed.effective_until || today(90),
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const toggle = (w: number) => setState((s) => ({ ...s, weekdays: s.weekdays.includes(w) ? s.weekdays.filter((x) => x !== w) : [...s.weekdays, w].sort() }));
    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            await api.post("/schedule/rules/bulk", {
                group_id: groupId,
                weekdays: state.weekdays,
                start_time: state.start_time,
                end_time: state.end_time,
                effective_from: state.effective_from,
                effective_until: state.effective_until || null,
                additive: false,
            });
            onSaved();
        } catch (e: any) {
            const raw = formatApiError(e);
            const detail = e?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.error : detail;
            const map: Record<string, string> = {
                teacher_conflict: t("groups.sched.err.teacher_conflict"),
                room_conflict: t("groups.sched.err.room_conflict"),
                group_conflict: t("groups.sched.err.group_conflict"),
                end_before_start: t("groups.sched.err.end_before_start"),
                invalid_date_range: t("groups.sched.err.invalid_date_range"),
                no_weekdays: t("groups.sched.err.no_weekdays"),
            };
            setErr(map[code] || raw);
        } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="schedule-editor">
            <div className="surface-card w-full max-w-md p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("groups.sched.edit")}</h2>
                    <button onClick={onClose}><X size={18} style={{ color: "var(--text-muted)" }} /></button>
                </div>
                <div className="space-y-3">
                    <div className="rounded-md p-2 text-xs" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }}>{t("groups.sched.preserve_hint")}</div>
                    <div>
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.weekdays")}</div>
                        <div className="flex flex-wrap gap-1" data-testid="sched-weekdays">
                            {[0, 1, 2, 3, 4, 5, 6].map((w) => {
                                const on = state.weekdays.includes(w);
                                return <button key={w} type="button" data-testid={`sched-wd-${w}`} onClick={() => toggle(w)}
                                    className="rounded-full px-2 py-1 text-xs border" style={{ background: on ? "var(--selected-bg)" : "var(--card)", color: "var(--text)", borderColor: on ? "var(--primary)" : "var(--border)" }}>{t(`weekday.${w}` as any)}</button>;
                            })}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.start_time")}</div><input data-testid="sched-start-time" type="time" value={state.start_time} onChange={(e) => setState({ ...state, start_time: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.end_time")}</div><input data-testid="sched-end-time" type="time" value={state.end_time} onChange={(e) => setState({ ...state, end_time: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.effective_from")}</div><input data-testid="sched-from" type="date" value={state.effective_from} onChange={(e) => setState({ ...state, effective_from: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.effective_until")}</div><input data-testid="sched-until" type="date" value={state.effective_until} onChange={(e) => setState({ ...state, effective_until: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} /></label>
                    </div>
                    {err && <div className="text-sm rounded-md p-2" data-testid="sched-err" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                    <button data-testid="sched-save-btn" disabled={saving || state.weekdays.length === 0} onClick={submit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{saving ? <Loader2 className="animate-spin" size={16} /> : t("common.save")}</button>
                </div>
            </div>
        </div>
    );
}

function EditCreditsDialog({ row, onClose, onSaved }: { row: any; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const [total, setTotal] = useState<string>(row.lesson_credits_total == null ? "" : String(row.lesson_credits_total));
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            const parsed = total === "" ? null : parseInt(total, 10);
            if (parsed !== null && (isNaN(parsed) || parsed < 0)) {
                setErr(t("roster.credits.err_invalid", { defaultValue: "0 və ya böyük tam ədəd daxil edin." }));
                setSaving(false); return;
            }
            await api.patch(`/enrollments/${row.enrollment_id}/lesson-credits`, { lesson_credits_total: parsed });
            onSaved();
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="edit-credits-dialog">
            <div className="surface-card w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("roster.credits.title", { defaultValue: "Dərs yükünü düzəlt" })}</h2>
                    <button onClick={onClose} data-testid="edit-credits-close"><X size={18} style={{ color: "var(--text-muted)" }} /></button>
                </div>
                <div className="space-y-3">
                    <div className="text-sm" style={{ color: "var(--text-muted)" }}>{row.person_name}</div>
                    <label className="block">
                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("roster.credits_total", { defaultValue: "Dərs yükü" })}</div>
                        <input data-testid="edit-credits-input" type="number" min={0} value={total}
                            onChange={(e) => setTotal(e.target.value)}
                            placeholder={String(t("roster.credits.blank_hint", { defaultValue: "Boş buraxın — izlənmir" }))}
                            className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    </label>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("roster.credits.hint", { defaultValue: "Yalnız bu şagirdin qalıq balansına təsir edir; qrup dəyişikliyi ilə avtomatik pozulmur." })}</div>
                    {err && <div className="text-sm rounded-md p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="edit-credits-err">{err}</div>}
                    <button data-testid="edit-credits-save" disabled={saving} onClick={submit}
                        className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60"
                        style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                        {saving ? <Loader2 className="animate-spin" size={16} /> : t("common.save")}
                    </button>
                </div>
            </div>
        </div>
    );
}
